import { __decorate, __metadata } from "tslib";
import { I18nThemed } from 'interfaces/mixins';
import { lccstore } from 'lcc-state';
import timesync from 'timesync';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import has from '@dojo/framework/core/has';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import Button from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import Label from '@dojo/widgets/label';
import Radio from '@dojo/widgets/radio';
import { convertToBoolean } from '@holmescorp/common';
import css from '../css/mcquestion.m.css';
import { setQuestion } from '../support/state';
import Correct from '../widgets/correct';
import { CountdownTimer } from '../widgets/countdown-timer';
const ISSEEN = 0;
const ISCOMPLETE = 1;
const ISCORRECT = 2;
let MCQuestionBase = class MCQuestionBase extends I18nThemed {
    _propsInited = false;
    _lastQuestionId = '';
    _inSubmit = false;
    _inFinish = false;
    _inNext = false;
    modifyQuestion(htmlText) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlText;
        const strongTag = tempDiv.querySelector('strong');
        if (strongTag && strongTag.parentElement?.matches('div')) {
            const parentNode = strongTag.parentNode;
            const h2Tag = document.createElement('h2');
            h2Tag.appendChild(strongTag);
            parentNode?.insertBefore(h2Tag, parentNode.firstChild);
        }
        const divElements = tempDiv.querySelectorAll('div');
        const lastDiv = divElements[divElements.length - 1];
        if (lastDiv) {
            lastDiv.setAttribute('id', 'question-label');
        }
        const modifiedText = tempDiv.innerHTML;
        return modifiedText;
    }
    onChange(value) {
        const { question, quizstore } = this.properties;
        question.distractororder.forEach((key) => {
            question.distractordata[key].chosen = false;
        });
        question.distractordata[+value].chosen = true;
        const idx = question.distractororder.indexOf(+value);
        question.flags[ISCORRECT] = idx > -1 && question.flags[idx + 4];
        question.answer = value;
        setQuestion(quizstore)({ question });
        this.invalidate();
    }
    onAttach() {
        lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
    }
    ensure_nls(properties) {
        const { get, path } = lccstore;
        const dynls = get(path('dynls'));
        const bundle = dynls.get('quiz');
        if (!bundle) {
            properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} };
        }
        else {
            properties.localizedBundle = this.localizeBundle(bundle);
        }
        return properties;
    }
    update_result() {
        const { question, updater } = this.properties;
        const { icode = {}, status, cursor, answer } = question;
        question.flags[ISSEEN] = true;
        question.flags[ISCOMPLETE] = !!answer;
        const data = {
            answer,
            icode: { ...icode, _answer: answer },
            starttime: question.starttime,
            stoptime: timesync.now().toJSON(),
            isseen: true,
            ismarked: question.marked ? true : false,
            iscorrect: question.flags[ISCORRECT]
        };
        const qrlocator = {
            status,
            cursor,
            qrid: '',
            quiz: ''
        };
        const result = { qrlocator, data };
        return updater([result]);
    }
    submitted() {
        this.update_result();
        const { question, quizstore } = this.properties;
        question.status = 'feedback';
        setQuestion(quizstore)({ question });
        this.invalidate();
    }
    get_numberofquestions() {
        const { quizstore } = this.properties;
        const { get, path } = quizstore;
        return get(path('info')).numberofquestions;
    }
    render_hint(iscorrect) {
        const showflag = has('use-hints') && iscorrect;
        return v('div', { key: 'hint', classes: [this.theme(css.distractorHint)] }, [
            showflag ? v('i', { classes: ['far', 'fa-arrow-alt-circle-right'] }) : null
        ]);
    }
    render_buttons(which) {
        const { messages: { finish: finishText = '', next_question: nextText = '', submit: submitText = '', show_pretest_timer = 'true', show_posttest_timer = 'true' } } = this.properties.localizedBundle;
        const { quizstore, quizkind, onNextQuestion, onFinish, theme } = this.properties;
        const { islast = false, isfeedback = false } = which;
        let showTimer = false;
        if (quizkind == 'pretest') {
            showTimer = convertToBoolean(show_pretest_timer);
        }
        else if (quizkind == 'posttest') {
            showTimer = convertToBoolean(show_posttest_timer);
        }
        const timer = v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [
            v('div', { classes: [this.theme(css.timer)] }, [w(CountdownTimer, { quizstore, countdown: '' })])
        ]);
        const buttons = isfeedback
            ?
                islast
                    ? v('div', { classes: [this.theme(css.primaryButton)] }, [
                        w(Button, {
                            theme,
                            key: 'finish',
                            onClick: async () => {
                                if (!this._inFinish) {
                                    this._inFinish = true;
                                    await this.update_result();
                                    onFinish();
                                }
                            }
                        }, [finishText])
                    ])
                    : v('div', { classes: [this.theme(css.primaryButton)] }, [
                        w(Button, {
                            theme,
                            key: 'next',
                            onClick: () => {
                                if (!this._inNext) {
                                    this._inNext = true;
                                    this.update_result();
                                    onNextQuestion();
                                }
                            }
                        }, [
                            v('span', {}, [
                                nextText,
                                v('i', { classes: ['fas', 'fa-arrow-right', this.theme(css.actionIcon)] })
                            ])
                        ])
                    ])
            :
                v('div', { classes: [this.theme(css.primaryButton)] }, [
                    w(Button, {
                        theme,
                        key: 'submit',
                        onClick: () => {
                            if (!this._inSubmit) {
                                this._inSubmit = true;
                                this.submitted();
                            }
                        }
                    }, [submitText])
                ]);
        return [buttons, showTimer ? timer : null];
    }
    render_question() {
        const { question, theme } = this.properties;
        const { distractororder, distractordata, text, id, questiontype, flags } = question;
        const numberofquestions = this.get_numberofquestions();
        const islast = question.cursor + 1 === numberofquestions;
        const { messages } = this.properties.localizedBundle;
        const distractorLegend = messages['distractor_legend'] || '';
        const modifiedText = this.modifyQuestion(text);
        const distractors = distractororder.map((which, idx) => {
            const { text, chosen } = distractordata[which];
            const widgetId = `d-${which}`;
            return v('div', {
                key: `${which}`,
                classes: [this.theme(css.distractor)]
            }, [
                this.render_hint(flags[idx + 4]),
                w(Radio, {
                    theme,
                    widgetId,
                    name: `mc-distractor`,
                    value: `${which}`,
                    checked: chosen,
                    onValue: () => this.onChange(`${which}`)
                }),
                w(Label, { forId: widgetId, theme }, [
                    v('div', { classes: [this.theme(css.distractorLabel)], innerHTML: text })
                ])
            ]);
        });
        return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
            v('div', { key: 'question-text', classes: [this.theme(css.question)], innerHTML: modifiedText }),
            v('fieldset', { key: 'distractors-fieldset', classes: [this.theme(css.distractorFieldset)] }, [
                v('legend', { classes: [this.theme(css.distractorLegend)] }, [distractorLegend]),
                v('div', {
                    key: 'distractors',
                    role: 'group',
                    ...formatAriaProperties({ labelledby: 'question-label' }),
                    classes: [this.theme(css.distractorBlock)]
                }, distractors)
            ]),
            v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
                v('div', { key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] }, this.render_buttons({ islast }))
            ])
        ]);
    }
    render_feedback() {
        const { messages: { correct: correctText = '', incorrect: incorrectText = '', rationale: rationaleText = '', for_more_info: fmiText = '', distractor_legend: distractorLegend = '' } } = this.properties.localizedBundle;
        const { question } = this.properties;
        const { distractororder, distractordata, flags, text, feedback, refname, id, questiontype, answer } = question;
        const numberofquestions = this.get_numberofquestions();
        const islast = question.cursor + 1 === numberofquestions;
        const modifiedText = this.modifyQuestion(text);
        if (!answer) {
            distractororder.push(0);
        }
        const distractors = distractororder.map((which, idx) => {
            const { text, chosen } = which > 0
                ? distractordata[which]
                : {
                    text: '<div>No Answer</div>',
                    chosen: true
                };
            const iscorrect = which > 0 ? flags[idx + 4] : false;
            const hasflag = iscorrect || (chosen && !iscorrect);
            const widgetId = `d-${which}`;
            return v('div', { key: `${which}`, classes: [this.theme(css.distractor)] }, [
                v('div', { key: 'hint', classes: [this.theme(css.distractorHint)] }),
                w(Radio, {
                    widgetId,
                    disabled: true,
                    name: `mc-distractor`,
                    value: `${which}`,
                    checked: chosen,
                    onValue: () => {
                    }
                }),
                w(Label, { forId: widgetId }, [
                    iscorrect ? w(Correct, { key: which, iscorrect, title: correctText }) : null,
                    chosen && !iscorrect ? w(Correct, { key: which, iscorrect, title: incorrectText }) : null,
                    v('div', {
                        classes: [this.theme(css.distractorLabel), hasflag ? this.theme(css.distractorWithFlag) : null],
                        innerHTML: text
                    })
                ])
            ]);
        });
        return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
            v('div', { key: 'feedback-block', classes: [this.theme(css.feedback)] }, [
                v('h2', { key: 'rationale', classes: [this.theme(css.rationale)] }, [rationaleText]),
                v('div', { innerHTML: feedback }),
                v('p', { classes: [this.theme(css.fmi)] }, [`${fmiText} ${refname}`])
            ]),
            v('div', {
                key: 'question-text',
                classes: [this.theme(css.question)],
                innerHTML: modifiedText
            }),
            v('fieldset', { key: 'distractors-fieldset', classes: [this.theme(css.distractorFieldset)] }, [
                v('legend', { classes: [this.theme(css.distractorLegend)] }, [distractorLegend]),
                v('div', {
                    key: 'distractors',
                    role: 'group',
                    ...formatAriaProperties({ labelledby: 'question-label' }),
                    classes: [this.theme(css.distractorBlock)]
                }, distractors)
            ]),
            v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
                v('div', { key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] }, this.render_buttons({ islast, isfeedback: true }))
            ])
        ]);
    }
    render() {
        const { isPlaceholder } = this.properties.localizedBundle;
        if (isPlaceholder) {
            return null;
        }
        const { question } = this.properties;
        if (!question) {
            return null;
        }
        const { status } = question;
        return status === 'question' ? this.render_question() : this.render_feedback();
    }
    stateProperties(properties) {
        const { quizstore } = properties;
        const { get, path } = quizstore;
        if (!this._propsInited) {
            const paths = [['question'], ['quiz'], ['loaded']];
            const invalidatePaths = paths.map((path) => quizstore.path(path.join('/')));
            const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
            this.own({
                destroy: () => {
                    handle.remove();
                }
            });
            const h2 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
            this.own({
                destroy: () => {
                    h2.remove();
                }
            });
            this._propsInited = true;
        }
        properties.quizkind = get(path('quiz')).kind;
        properties.question = get(path('question'));
        if (properties.question?.id !== this._lastQuestionId) {
            this._lastQuestionId = properties.question?.id || '';
            this._inSubmit = false;
            this._inFinish = false;
            this._inNext = false;
        }
        properties.loaded = get(path('loaded'));
        return properties;
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], MCQuestionBase.prototype, "ensure_nls", null);
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], MCQuestionBase.prototype, "stateProperties", null);
MCQuestionBase = __decorate([
    theme(css)
], MCQuestionBase);
export { MCQuestionBase };
export default MCQuestionBase;
